import React, { Suspense, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { useDispatch } from 'react-redux';
import { profile } from 'http/auth';
import { getCompanyInfoBySlug, initializeCompanyId } from 'http/companies';
import { actions } from 'redux/auth';
import { ThemeProvider } from '@material-ui/core/styles';
import { actions as stationsActions } from 'redux/stations';
import { getAppStage, getBaseDomain, getSubdomain, getTestSlug } from 'utils';
import Loader from 'components/Loader';
import { ToastContainer } from 'react-toastify';
import AppRouter from './router';
import { ROLES } from './appConstants';
import theme from './theme';
import 'react-toastify/dist/ReactToastify.min.css';

let themeCreator = theme();

const App = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [getCompanyInfoState, getCompanyInfo] = useAsyncFn(async () => {
    // If REACT_APP_TEST_SLUG is set, use it directly, otherwise use subdomain detection
    const testSlug = getTestSlug();
    const subdomain = testSlug ? testSlug : getSubdomain(window.location.hostname);

    if (subdomain) {
      const companyData = await getCompanyInfoBySlug(subdomain);
      if (!companyData.error && companyData) {
        dispatch(actions.setCompanyInfo(companyData));
        themeCreator = theme(companyData.mainColor, companyData.fontMainColor);
      }
    }
  });

  const redirectIfWrongSubdomain = companyData => {
    // Skip subdomain redirect in development mode
    if (getAppStage() === 'development') {
      return;
    }

    // If REACT_APP_TEST_SLUG is set, don't redirect (we're using single domain setup)
    const testSlug = getTestSlug();
    if (testSlug) {
      return;
    }

    const subdomain = getSubdomain(window.location.hostname);
    const { protocol, hostname } = window.location;
    const baseDomain = getBaseDomain(hostname);

    if (!companyData) {
      if (subdomain !== '') {
        window.location.replace(`${protocol}//${baseDomain}${location.pathname}`);
      }
    } else if (subdomain !== companyData.slug) {
      window.location.replace(`${protocol}//${companyData.slug}.${baseDomain}${location.pathname}`);
    }
  };

  const [state, getAuthInfo] = useAsyncFn(async () => {
    // Initialize companyId immediately if REACT_APP_TEST_SLUG is set
    await initializeCompanyId();

    await getCompanyInfo();

    let profileData;
    if (location.pathname !== '/password/set') {
      profileData = await profile();
    }

    if (profileData && !profileData.error) {
      redirectIfWrongSubdomain(profileData.company);
      dispatch(actions.authenticate(profileData));
      if (profileData.role !== ROLES.HYPER) {
        const stations = Array.isArray(profileData.stations) ? profileData.stations : [];
        if (stations.length) {
          dispatch(actions.setStation({ name: stations[0].name, id: stations[0].id }));
          dispatch(stationsActions.setStations(stations));
        } else {
          dispatch(stationsActions.setStations([]));
        }
      }

      history.push(location.pathname);
    }
  });

  useEffect(() => {
    getAuthInfo();
  }, []);

  const isLoading = getCompanyInfoState.loading || state.loading;

  return isLoading ? (
    <Loader />
  ) : (
    <Suspense fallback={<Loader />}>
      <ThemeProvider theme={themeCreator}>
        <AppRouter />
        <ToastContainer />
      </ThemeProvider>
    </Suspense>
  );
};

export default App;
